import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { OpinlyContent } from "@opinly/react";
import type { OpinlyNode } from "@opinly/shared";
import { buildBlogPostingJsonLd, buildMetadata, imageUrl } from "@opinly/shared";
import { blogPostQuery } from "@/lib/opinly-queries";
import type { BlogPostData } from "@/lib/opinly";
import { formatPostDate, opinlyClassNames, opinlyConfig, SITE_URL } from "@/lib/opinly";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ context, params }) => {
    const post = await context.queryClient.ensureQueryData(blogPostQuery(params.slug));
    if (!post) throw notFound();
    return post;
  },
  head: ({ params, loaderData }) => {
    const canonical = `${SITE_URL}/blog/${params.slug}`;
    if (!loaderData) {
      return {
        meta: [{ title: "Article unavailable, Casa Mi Pajobe" }, { name: "robots", content: "noindex" }],
      };
    }
    const post = loaderData as BlogPostData;
    const meta = buildMetadata({ type: "post", data: post }, opinlyConfig);
    const ogImage = post.titleFile?.fileKey
      ? imageUrl(post.titleFile.fileKey, opinlyConfig)
      : meta.ogImage;
    return {
      meta: [
        { title: meta.title },
        { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
        ...(meta.description ? [{ name: "description", content: meta.description }] : []),
        { property: "og:title", content: meta.title },
        ...(meta.description
          ? [{ property: "og:description", content: meta.description }]
          : []),
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
        ...(post.firstPublishedAt
          ? [{ property: "article:published_time", content: post.firstPublishedAt }]
          : []),
        ...(post.modifiedAt
          ? [{ property: "article:modified_time", content: post.modifiedAt }]
          : []),
        { name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" },
        ...(ogImage
          ? [
              { property: "og:image", content: ogImage },
              { name: "twitter:image", content: ogImage },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBlogPostingJsonLd(
              {
                title: post.title,
                description: post.description,
                content: post.content as OpinlyNode,
                firstPublishedAt: post.firstPublishedAt,
                modifiedAt: post.modifiedAt,
                author: post.author ? { name: post.author.name } : null,
                imageFileKey: post.titleFile?.fileKey ?? null,
              },
              opinlyConfig,
            ),
          ),
        },
      ],
    };
  },
  component: BlogPostPage,
  pendingComponent: PostPending,
  errorComponent: PostError,
  notFoundComponent: PostNotFound,
});

function PostPending() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-12">
      <div className="h-9 w-3/4 animate-pulse rounded bg-muted" />
      <div className="mt-6 aspect-[16/9] w-full animate-pulse rounded-xl bg-muted" />
      <div className="mt-8 space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-4 w-full animate-pulse rounded bg-muted" />
        ))}
      </div>
    </main>
  );
}

function BackToBlog() {
  return (
    <Link
      to="/blog"
      search={{}}
      className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
    >
      Back to the blog
    </Link>
  );
}

function PostNotFound() {
  return (
    <main className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Article not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This article doesn't exist or has been unpublished.
      </p>
      <BackToBlog />
    </main>
  );
}

function PostError({ error }: { error?: Error }) {
  if (error) console.error(error);
  return (
    <main className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="text-2xl font-semibold text-foreground">This article didn't load</h1>
      <p className="mt-2 text-sm text-muted-foreground">Please try again in a moment.</p>
      <BackToBlog />
    </main>
  );
}

function BlogPostPage() {
  const { slug } = Route.useParams();
  const { data: post } = useSuspenseQuery(blogPostQuery(slug));
  if (!post) return <PostNotFound />;

  const hero = post.titleFile?.fileKey ? imageUrl(post.titleFile.fileKey, opinlyConfig) : null;

  return (
    <main className="mx-auto max-w-3xl px-4 py-12">
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <Link to="/blog" search={{}} className="hover:underline">
          Blog
        </Link>
        <span aria-hidden> / </span>
        <span className="text-foreground">{post.title}</span>
      </nav>

      <article className="mt-6">
        <header>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {post.title}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {post.author?.name ? `${post.author.name} · ` : ""}
            <time dateTime={post.firstPublishedAt}>{formatPostDate(post.firstPublishedAt)}</time>
            {post.category?.name ? ` · ${post.category.name}` : ""}
          </p>
          {post.description ? (
            <p className="mt-4 text-lg text-foreground/80">{post.description}</p>
          ) : null}
          {hero ? (
            <img
              src={hero}
              alt={post.titleFile?.altText ?? post.title}
              className="mt-8 aspect-[16/9] w-full rounded-xl border border-border/60 object-cover"
            />
          ) : null}
        </header>

        <div className="mt-6">
          <OpinlyContent
            content={post.content as OpinlyNode}
            config={opinlyConfig}
            classNames={opinlyClassNames}
          />
        </div>

        {post.faqs?.length ? (
          <section className="mt-12" aria-labelledby="faq-heading">
            <h2 id="faq-heading" className="text-xl font-semibold text-foreground">
              Frequently asked questions
            </h2>
            <dl className="mt-4 space-y-4">
              {post.faqs.map((faq) => (
                <div key={faq.question} className="rounded-xl border border-border/60 p-4">
                  <dt className="font-medium text-foreground">{faq.question}</dt>
                  <dd className="mt-1 text-sm text-muted-foreground">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {post.tags?.length ? (
          <ul className="mt-10 flex flex-wrap gap-2" aria-label="Tags">
            {post.tags.map((tag) => (
              <li
                key={tag.slug}
                className="rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground"
              >
                {tag.name}
              </li>
            ))}
          </ul>
        ) : null}
      </article>

      <BackToBlog />
    </main>
  );
}
